import React from "react";
import _ from "lodash";
import {
  makeStyles,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { useSelector } from "react-redux";
import TaxonomyData from "../taxonomyData.json";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import {
  hardColors,
  textColor,
  headerTextSize,
  useStepThreeStyles,
  bodyTextSize,
  descriptionTextSize,
  borderRadiusValue,
  graphicsDarkRed,
  tooltipTextSize
} from "../lib/constants";

const useStyles = makeStyles(
  {
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      height: "95%",
      overflow: "auto",
      paddingTop: "8px"
    },
    card: {
      marginBottom: "10px",
      width: "calc(50% - 5px)"
    },
    flexRowWrap: {
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "center"
    },
    customChip: {
      margin: "5px",
      color: "white",
      cursor: "pointer",
      fontSize: (props) => (props.sizeBreak ? tooltipTextSize : descriptionTextSize)
    },
    header: {
      textAlign: "left",
      marginBottom: "2px",
      fontSize: headerTextSize
    },

    Accordion: {
      position: "relative",
      zIndex: 1,
      width: "100%",
      marginBottom: "5px"
    },
    paper: {
      borderRadius: borderRadiusValue + " !important",
      overflow: "hidden",
      marginBottom: "10px",
      marginTop: "5px",
      boxShadow: "0 1px 9px 1px rgb(0 0 0 / 6%)"
    },
    paperNotExpandable: {
      "&:hover": {
        cursor: "default !important"
      }
    },
    content: {
      "& $icon": {
        color: textColor,
        marginLeft: "auto"
      }
    },
    expanded: {
      "& $icon": {
        transform: "rotate(180deg)"
      }
    },

    icon: {
      color: textColor
    },

    detail: {
      paddingTop: 0,
      marginTop: 0
    },

    taxGroupNameStyling: {
      paddingTop: "2px",
      marginLeft: "4px",
      fontSize: (props) => (props.sizeBreak ? descriptionTextSize : bodyTextSize),
      fontWeight: "bold",
      textTransform: "uppercase"
    },

    undefinedDescription: {
      fontSize: (props) => (props.sizeBreak ? tooltipTextSize : descriptionTextSize)
    },

    headingText: {
      fontSize: "1.2rem"
    }
  },
  { name: "TaxonomyStyles" }
);

function Item(props) {
  const { sizeBreak } = props;
  const iconMargin = sizeBreak ? "0px" : "2px";
  const classes = useStyles({ sizeBreak: sizeBreak });

  const { heading, ind, expanded, handleChange } = props;

  const taxonomyGroups = useSelector((state) => state.taxonomyGroups);
  const taxonomyColors = useSelector((state) => state.taxonomyGroupColors);
  const checkedCardStepOne = useSelector((state) => state.checkedCardStepOne);
  const checkedCardStepTwo = useSelector((state) => state.checkedCardStepTwo);
  const checkedTags = useSelector((state) => state.checkedTags);

  return (
    <React.Fragment>
      <div className={classes.Accordion}>
        <div key={heading}>
          <Accordion expanded={expanded === ind} classes={{ root: classes.paper }} onChange={handleChange(ind)}>
            <AccordionSummary
              classes={{
                content: classes.content
              }}
            >
              <FiberManualRecordIcon style={{ color: hardColors[ind], marginTop: iconMargin }} />
              <Typography
                variant="subtitle2"
                component="h2"
                classes={{ root: classes.taxGroupNameStyling }}
                style={{ color: hardColors[ind] }}
              >
                {heading}
              </Typography>
              <ExpandMoreIcon className={classes.icon} />
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.detail }}>
              <div className={classes.flexRowWrap}>
                {TaxonomyData[heading].map((txt) => {
                  if (taxonomyGroups.length === 0 || taxonomyColors.length === 0) {
                    return <Chip key={`taxonomy-chip-${txt}`} />;
                  }
                  let index = -1;
                  let keys = Object.keys(TaxonomyData);
                  for (let i = 0; i < keys.length; i++) {
                    if (TaxonomyData[keys[i]].indexOf(txt) !== -1) {
                      index = i;
                      break;
                    }
                  }
                  return (
                    <Chip
                      className={classes.customChip}
                      label={txt}
                      // onClick={() => toggleCheck(txt)}
                      clickable={false}
                      style={{
                        cursor: "default",
                        backgroundColor: index === -1 ? "#DDD" : taxonomyColors[0][index],
                        boxShadow:
                          _.indexOf(checkedTags[checkedCardStepOne + " " + checkedCardStepTwo], txt) === -1
                            ? "0 0 0 5px transparent"
                            : "0 0 0 2px #FFF, 0 0 0 4px " + graphicsDarkRed //#0069d4"
                      }}
                      key={`taxonomy-chip-${txt}`}
                    />
                  );
                })}
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>
      {ind === 2 && (
        <div className={classes.Accordion}>
          <div key="unbestimmt">
            <Accordion expanded={expanded === 3} classes={{ root: classes.paper }} onChange={handleChange(3)}>
              <AccordionSummary
                classes={{
                  content: classes.content
                }}
              >
                <FiberManualRecordIcon style={{ color: hardColors[3], marginTop: iconMargin }} />
                <Typography
                  variant="subtitle2"
                  component="h2"
                  classes={{ root: classes.taxGroupNameStyling }}
                  style={{ color: hardColors[3] }}
                >
                  Unbestimmt
                </Typography>
                <ExpandMoreIcon className={classes.icon} />
              </AccordionSummary>
              <AccordionDetails classes={{ root: classes.detail }}>
                <Typography classes={{ root: classes.undefinedDescription }} style={{ color: hardColors[3] }}>
                  In dieser Gruppe landen Schlüsselwörter, die nicht nah genug an anderen Gruppen waren - also den Taxa
                  in den anderen Gruppen zu unähnlich waren.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}

export default function Taxonomy(props) {
  const { needResize } = props;
  const resize = needResize[0] || needResize[1];
  const classes = useStyles();
  const stepThreeStyles = useStepThreeStyles({ sizeBreak: resize });

  const tooltipText =
    "In diesem Szenario umfasst die bestehende Unternehmenstaxonomie drei Gruppen. In der 3D-Ansicht wird sie komplett dargestellt.";

  const [expanded, setExpanded] = React.useState(false);
  const handleChange = (ind) => (event, isExpanded) => {
    //prevent expanded set when the not expandable option in true
    setExpanded(isExpanded ? ind : false);
  };

  return (
    <div className={stepThreeStyles.root}>
      <div className={stepThreeStyles.headerContainer}>
        <Typography className={classes.headingText} variant="subtitle2">
          {needResize[0] ? "Bestehende Taxonomie" : " Bestehende Unternehmenstaxonomie"}
        </Typography>
        <Tooltip
          id="taxonomie-erklaerung"
          title={tooltipText}
          placement="bottom-start"
          classes={{ tooltip: stepThreeStyles.popoverStyling }}
        >
          <InfoOutlinedIcon classes={{ root: stepThreeStyles.infoIconStyling }} />
        </Tooltip>
      </div>
      {/* <Divider /> */}
      <div className={stepThreeStyles.contentContainer}>
        {Object.keys(TaxonomyData).map((key, index) => (
          <Item
            heading={key}
            key={`item-${index}`}
            ind={index}
            expanded={expanded}
            handleChange={handleChange}
            sizeBreak={resize}
          />
        ))}
      </div>
    </div>
  );
}
